import { useEffect, useState } from 'react';
import { BlogApi } from '../api/adminApi';
import { fallbackBlogs, shapeBlog } from './blogHelpers';
import { getCachedBlogById, getCachedBlogBySlug, primeBlogCache } from './useBlogs';

const findFallbackById = (id) => {
  if (!id) return null;
  return fallbackBlogs.find((item) => String(item.id) === String(id)) || null;
};

const findFallbackBySlug = (slug) => {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return fallbackBlogs.find((item) => item.slug === normalized) || null;
};

export const useBlogDetails = ({ blogId, slug } = {}) => {
  const normalizedId = blogId ? String(blogId) : '';
  const normalizedSlug = slug ? slug.trim().toLowerCase() : '';

  const useSlugEndpoint = Boolean(normalizedSlug) && !normalizedId;

  const resolveInitialBlog = () => {
    if (normalizedSlug) {
      const viaSlug = getCachedBlogBySlug(normalizedSlug) || findFallbackBySlug(normalizedSlug);
      if (viaSlug) return viaSlug;
    }
    if (normalizedId) {
      return getCachedBlogById(normalizedId) || findFallbackById(normalizedId);
    }
    return null;
  };

  const initialBlog = resolveInitialBlog();
  const [blog, setBlog] = useState(initialBlog);
  const [isLoading, setIsLoading] = useState(!initialBlog);
  const [error, setError] = useState('');
  const [isFallback, setIsFallback] = useState(Boolean(initialBlog && !getCachedBlogById(normalizedId) && !getCachedBlogBySlug(normalizedSlug)));

  useEffect(() => {
    if (!normalizedId && !normalizedSlug) {
      setBlog(null);
      setIsLoading(false);
      setError('Missing blog identifier.');
      setIsFallback(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError('');

    const cachedBlog = useSlugEndpoint
      ? getCachedBlogBySlug(normalizedSlug)
      : getCachedBlogById(normalizedId);
    if (cachedBlog) {
      setBlog(cachedBlog);
      setIsFallback(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fallbackBlog = useSlugEndpoint
      ? findFallbackBySlug(normalizedSlug) || findFallbackById(normalizedId)
      : findFallbackById(normalizedId);
    if (fallbackBlog) {
      setBlog(fallbackBlog);
      setIsFallback(true);
    } else {
      setBlog(null);
      setIsFallback(false);
    }

    const fetchPromise = useSlugEndpoint
      ? BlogApi.getBySlug(normalizedSlug)
      : BlogApi.getOne(normalizedId || normalizedSlug);

    fetchPromise
      .then((data) => {
        if (!isMounted) return;
        const shaped = shapeBlog(data);
        setBlog(shaped);
        setIsFallback(false);
        primeBlogCache([shaped]);
      })
      .catch((apiError) => {
        if (!isMounted) return;
        setError(apiError?.response?.data?.message || apiError.message || 'Blog not found.');
        if (!fallbackBlog) {
          const fallback = useSlugEndpoint
            ? findFallbackBySlug(normalizedSlug) || findFallbackById(normalizedId)
            : findFallbackById(normalizedId);
          if (fallback) {
            setBlog(fallback);
            setIsFallback(true);
          }
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [normalizedId, normalizedSlug, useSlugEndpoint]);

  return { blog, isLoading, error, isFallback };
};
